import { Box, InputLabel, Stack, Typography, useColorScheme } from '@mui/material';
import Chip from '@mui/material/Chip';
import { Trans, useTranslation } from 'react-i18next';
import HelpPopover from '../../../HelpPopover';
import { updateParameter } from '../DeviceCardBuilder';
import { DeviceCardProps } from '../DeviceCardProps';
import { useControlInitialization } from '../../../../hooks/controls/useControlInitialization';
import { isDarkColorScheme } from '../../../../utils/colorScheme';
import { BLACK, BPS_BLUE, WHITE } from '../../../../theme/theme-colors';
import HighSpeedMotorIcon from '../../../Drawers/assets/motor/high-speed-motor-mech-icon.svg?react';
import HighTorqueMotorIcon from '../../../Drawers/assets/motor/high-torque-motor-mech.svg?react';
import UnsupportedCodeStatus from './UnsupportedCodeStatus';
import { isUnsupportedEnumRawValue, parseKnownEnumRawValue } from './unsupportedEnumValue';

type MotorTypeControlProps = DeviceCardProps;

const MOTOR_TYPE_HIGH_SPEED = 'MotorType.HIGH_SPEED';
const MOTOR_TYPE_HIGH_TORQUE = 'MotorType.HIGH_TORQUE';
const MOTOR_TYPE_VALUES = [MOTOR_TYPE_HIGH_SPEED, MOTOR_TYPE_HIGH_TORQUE] as const;

type MotorTypeValue = typeof MOTOR_TYPE_HIGH_SPEED | typeof MOTOR_TYPE_HIGH_TORQUE;

const MotorTypeControl = (props: MotorTypeControlProps) => {
  const { t } = useTranslation();
  const { mode, systemMode } = useColorScheme();
  const isDarkMode = isDarkColorScheme(mode, systemMode);
  const {
    value: motorType,
    setValue: setMotorType,
    unsupportedRawValue,
  } = useControlInitialization<MotorTypeValue>(props, {
    parseValue: (v) => parseKnownEnumRawValue(v, MOTOR_TYPE_VALUES, MOTOR_TYPE_HIGH_SPEED),
    isUnsupportedRawValue: (rawValue) => isUnsupportedEnumRawValue(rawValue, MOTOR_TYPE_VALUES),
  });

  const handleSelect = (selected: MotorTypeValue) => {
    setMotorType(selected);
    updateParameter(props, selected);
  };

  const isUnsupported = Boolean(unsupportedRawValue);
  const safeValue = isUnsupported ? undefined : motorType ?? MOTOR_TYPE_HIGH_SPEED;
  const isHighSpeed = safeValue === MOTOR_TYPE_HIGH_SPEED;
  const isHighTorque = safeValue === MOTOR_TYPE_HIGH_TORQUE;

  const selectedIconColor = isDarkMode ? BLACK : WHITE;
  const unselectedIconColor = BPS_BLUE;

  return (
    <Stack direction="column" spacing={1.5}>
      <InputLabel id="motor-type-chips-label">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography>
            <Trans i18nKey="inputControl.motor_type">Motor Type</Trans>
          </Typography>
          <HelpPopover id="motor-type-popover" text={t('inputControl.motor_type_info_tooltip')} />
        </Stack>
      </InputLabel>
      <UnsupportedCodeStatus
        code={unsupportedRawValue}
        descriptionI18nKey="controlPanel.devices.unsupportedEnumParameter.description"
        descriptionDefaultValue="Unsupported setting value: {{code}}"
      />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          label={<Trans i18nKey="motorTypeControl.high_speed">High-Speed</Trans>}
          icon={
            <Box
              component={HighSpeedMotorIcon}
              aria-hidden
              sx={{
                width: 18,
                height: 18,
                color: isHighSpeed ? selectedIconColor : unselectedIconColor,
              }}
            />
          }
          onClick={() => handleSelect(MOTOR_TYPE_HIGH_SPEED)}
          color={isHighSpeed ? 'primary' : 'default'}
          variant={isHighSpeed ? 'filled' : 'outlined'}
          aria-pressed={isHighSpeed}
          role="button"
        />
        <Chip
          label={<Trans i18nKey="motorTypeControl.high_torque">High-Torque</Trans>}
          icon={
            <Box
              component={HighTorqueMotorIcon}
              aria-hidden
              sx={{
                width: 18,
                height: 18,
                color: isHighTorque ? selectedIconColor : unselectedIconColor,
              }}
            />
          }
          onClick={() => handleSelect(MOTOR_TYPE_HIGH_TORQUE)}
          color={isHighTorque ? 'primary' : 'default'}
          variant={isHighTorque ? 'filled' : 'outlined'}
          aria-pressed={isHighTorque}
          role="button"
        />
      </Stack>
    </Stack>
  );
};

export default MotorTypeControl;
